import { Component, ElementRef, OnInit, ViewChild, Input, Output, EventEmitter } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';

export interface UploadConfig {
  accept: string;
  maxSize?: number;
  multiple?: boolean;
  title: string;
  subtitle: string;
  description: string;
  fileTypes: string;
}

@Component({
  selector: 'app-upload-files',
  standalone: false,
  templateUrl: './upload-files.component.html',
  styleUrl: './upload-files.component.scss'
})
export class UploadFilesComponent implements OnInit {
  @ViewChild('fileInput') fileInput!: ElementRef;
  @Input() uploadConfig!: UploadConfig;
  @Input() isUploading = false;
  @Output() filesSelected = new EventEmitter<File[]>();

  errorMessage: string = '';
  isDragging = false;

  constructor(
    private translate: TranslateService
  ) { }

  ngOnInit() {
  }

  openFileInput() {
    if (!this.isUploading) {
      this.fileInput.nativeElement.click();
    }
  }

  async handleFileUpload(event: Event) {
    this.errorMessage = '';
    const inputElement = event.target as HTMLInputElement;
    if (inputElement.files && inputElement.files.length > 0) {
      const files = Array.from(inputElement.files);
      if (this.validateFiles(files)) {
        this.filesSelected.emit(files);
        inputElement.value = '';
      }
    }
  }

  onDragOver(event: DragEvent) {
    event.preventDefault();
    event.stopPropagation();
    this.isDragging = true;
  }

  onDragLeave(event: DragEvent) {
    event.preventDefault();
    event.stopPropagation();
    this.isDragging = false;
  }

  async onDrop(event: DragEvent) {
    event.preventDefault();
    event.stopPropagation();
    this.isDragging = false;
    this.errorMessage = '';
    if (event.dataTransfer && event.dataTransfer.files.length > 0) {
      const files = Array.from(event.dataTransfer.files);
      if (this.validateFiles(files)) {
        this.filesSelected.emit(files);
        if (this.fileInput && this.fileInput.nativeElement) {
          this.fileInput.nativeElement.value = '';
        }
      }
    }
  }

  private validateFiles(files: File[]): boolean {
    if (!this.uploadConfig.multiple && files.length > 1) {
      this.errorMessage = this.translate.instant('FILE_UPLOAD_POPOVER.ERROR_ONLY_ONE');
      return false;
    }

    const acceptList = this.uploadConfig.accept.split(',').map(ext => ext.trim().toLowerCase());
    for (const file of files) {
      const ext = '.' + file.name.split('.').pop()?.toLowerCase();
      if (acceptList.length > 0 && acceptList[0] !== '' && !acceptList.includes(ext)) {
        this.errorMessage = this.translate.instant('FILE_UPLOAD_POPOVER.ERROR_TYPE', { file: file.name });
        return false;
      }
      if (this.uploadConfig.maxSize && file.size > this.uploadConfig.maxSize * 1024 * 1024) {
        this.errorMessage = this.translate.instant('FILE_UPLOAD_POPOVER.ERROR_SIZE', { file: file.name, max: this.uploadConfig.maxSize });
        return false;
      }
    }
    return true;
  }
}
